
import React, { useState } from 'react';
import { Users } from 'lucide-react';
import FormInput from './FormInput';
import MultiItemInput from './MultiItemInput';
import SelectInput from './SelectInput';
import DocumentUpload from './DocumentUpload';
import { CampaignFormData, UploadedDocument } from '../types';

interface CampaignFormProps {
  onSubmit: (data: CampaignFormData) => void;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState<CampaignFormData>({
    core_issue_details: {
      campaign_name: '',
      decision_type: 'Corporate Policy',
      campaign_status: 'Planning',
      campaign_start_date: '',
      target_company: '',
      labor_issue: '',
      issue_description: '',
    },
    documents_and_links: {
      news_links: [],
      reference_links: [],
      uploaded_documents: [],
    },
    stakeholders: {
      corporate_executives: [],
      company_representatives: [],
      union_leaders: [],
      worker_allies: [],
      community_supporters: [],
      corporate_interests: [],
      workplace_dynamics: '',
    },
    corporate_and_strategic: {
      corporate_motivations: '',
      anti_union_tactics: '',
      corporate_vulnerabilities: '',
    },
    campaign_outcome: {
      primary_goal: '',
      target_audience: '',
    },
  });
  
  const [activeSection, setActiveSection] = useState('core_issue_details');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (section: keyof CampaignFormData, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [field]: value,
      },
    }));
    
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: '',
      }));
    }
  };

  const handleArrayInputChange = (section: keyof CampaignFormData, field: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [field]: value,
      },
    }));
  };

  const handleDocumentUpload = (document: UploadedDocument) => {
    setFormData(prev => ({
      ...prev,
      documents_and_links: {
        ...prev.documents_and_links,
        uploaded_documents: [...prev.documents_and_links.uploaded_documents, document],
      },
    }));
  };

  const handleDocumentRemove = (documentId: string) => {
    setFormData(prev => ({
      ...prev,
      documents_and_links: {
        ...prev.documents_and_links,
        uploaded_documents: prev.documents_and_links.uploaded_documents.filter(
          doc => doc.id !== documentId
        ),
      },
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.core_issue_details.campaign_name) {
      newErrors['core_issue_details.campaign_name'] = 'Campaign name is required';
    }
    
    if (!formData.core_issue_details.target_company) {
      newErrors['core_issue_details.target_company'] = 'Target company is required';
    }
    
    if (!formData.core_issue_details.labor_issue) {
      newErrors['core_issue_details.labor_issue'] = 'Labor issue is required';
    }
    
    if (!formData.core_issue_details.issue_description) {
      newErrors['core_issue_details.issue_description'] = 'Issue description is required';
    }
    
    if (!formData.campaign_outcome.primary_goal) {
      newErrors['campaign_outcome.primary_goal'] = 'Primary goal is required';
    }
    
    if (!formData.campaign_outcome.target_audience) {
      newErrors['campaign_outcome.target_audience'] = 'Target audience is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await onSubmit(formData);
      } catch (error) {
        console.error('Form submission error:', error);
        setIsSubmitting(false);
      }
    } else {
      const errorSections = Object.keys(errors).map(key => key.split('.')[0]);
      if (errorSections.length > 0) {
        const firstErrorSection = errorSections[0] as keyof CampaignFormData;
        setActiveSection(firstErrorSection);
      }
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'core_issue_details':
        return (
          <div className="animate-fade-in">
            <h3 className="text-lg font-medium mb-4">Core Campaign Details</h3>
            
            <FormInput
              id="campaign_name"
              label="Campaign Name"
              value={formData.core_issue_details.campaign_name}
              onChange={(e) => handleInputChange('core_issue_details', 'campaign_name', e.target.value)}
              required
              error={errors['core_issue_details.campaign_name']}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectInput
                id="decision_type"
                label="Type of Corporate Issue"
                options={['Corporate Policy', 'Workplace Conditions', 'Union Recognition', 'Contract Negotiation', 'Anti-Union Campaign', 'Other']}
                value={formData.core_issue_details.decision_type}
                onChange={(e) => handleInputChange('core_issue_details', 'decision_type', e.target.value)}
              />
              
              <SelectInput
                id="campaign_status"
                label="Current Campaign Status"
                options={['Planning', 'Active', 'Stalled', 'Escalating', 'Resolved', 'Other']}
                value={formData.core_issue_details.campaign_status}
                onChange={(e) => handleInputChange('core_issue_details', 'campaign_status', e.target.value)}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                id="campaign_start_date"
                label="Campaign Start Date (if applicable)"
                type="date"
                value={formData.core_issue_details.campaign_start_date || ''}
                onChange={(e) => handleInputChange('core_issue_details', 'campaign_start_date', e.target.value)}
              />
              
              <FormInput
                id="target_company"
                label="Target Company"
                value={formData.core_issue_details.target_company}
                onChange={(e) => handleInputChange('core_issue_details', 'target_company', e.target.value)}
                required
                error={errors['core_issue_details.target_company']}
              />
            </div>
            
            <FormInput
              id="labor_issue"
              label="Key Labor Issue"
              placeholder="e.g., wage theft, unsafe working conditions, union busting, contract violations"
              value={formData.core_issue_details.labor_issue}
              onChange={(e) => handleInputChange('core_issue_details', 'labor_issue', e.target.value)}
              required
              error={errors['core_issue_details.labor_issue']}
            />
            
            <FormInput
              id="issue_description"
              label="Brief Description of the Issue"
              placeholder="Provide 1-3 paragraphs describing the workplace issue and campaign goals"
              value={formData.core_issue_details.issue_description}
              onChange={(e) => handleInputChange('core_issue_details', 'issue_description', e.target.value)}
              multiline
              rows={5}
              required
              error={errors['core_issue_details.issue_description']}
            />
          </div>
        );
      
      case 'documents_and_links':
        return (
          <div className="animate-fade-in">
            <h3 className="text-lg font-medium mb-4">Key Documents & Links</h3>
            
            <MultiItemInput
              id="news_links"
              label="Relevant News Articles or Company Statements"
              placeholder="Enter URL and press Enter or Add"
              items={formData.documents_and_links.news_links}
              onChange={(items) => handleArrayInputChange('documents_and_links', 'news_links', items)}
            />
            
            <MultiItemInput
              id="reference_links"
              label="Links to Company Reports, SEC Filings, or Research"
              placeholder="Enter URL and press Enter or Add"
              items={formData.documents_and_links.reference_links}
              onChange={(items) => handleArrayInputChange('documents_and_links', 'reference_links', items)}
            />

            <DocumentUpload
              onDocumentUpload={handleDocumentUpload}
              onDocumentRemove={handleDocumentRemove}
              documents={formData.documents_and_links.uploaded_documents}
              className="mt-6"
            />
          </div>
        );
      
      case 'stakeholders':
        return (
          <div className="animate-fade-in">
            <h3 className="text-lg font-medium mb-4">Stakeholders</h3>
            
            <MultiItemInput
              id="corporate_executives"
              label="Key Corporate Executives and Decision-Makers"
              placeholder="e.g., CEO Johnson, VP of HR Smith, Board Chair Wilson"
              items={formData.stakeholders.corporate_executives}
              onChange={(items) => handleArrayInputChange('stakeholders', 'corporate_executives', items)}
            />
            
            <MultiItemInput
              id="company_representatives"
              label="Company Representatives and Managers"
              placeholder="e.g., Regional Manager, Plant Supervisor, Corporate Counsel"
              items={formData.stakeholders.company_representatives}
              onChange={(items) => handleArrayInputChange('stakeholders', 'company_representatives', items)}
            />
            
            <MultiItemInput
              id="union_leaders"
              label="Union Leaders and Organizers"
              placeholder="e.g., Local Union President, Organizer Davis, Shop Steward"
              items={formData.stakeholders.union_leaders}
              onChange={(items) => handleArrayInputChange('stakeholders', 'union_leaders', items)}
            />
            
            <MultiItemInput
              id="worker_allies"
              label="Worker Allies and Supporters"
              placeholder="e.g., Worker Committee Members, Activist Employees"
              items={formData.stakeholders.worker_allies}
              onChange={(items) => handleArrayInputChange('stakeholders', 'worker_allies', items)}
            />
            
            <MultiItemInput
              id="community_supporters"
              label="Community Organizations and Supporters"
              placeholder="e.g., Community Alliance, Faith Leaders, Political Allies"
              items={formData.stakeholders.community_supporters}
              onChange={(items) => handleArrayInputChange('stakeholders', 'community_supporters', items)}
            />
            
            <MultiItemInput
              id="corporate_interests"
              label="Corporate Financial Interests and Dependencies"
              placeholder="e.g., major investors, key customers, financial institutions"
              items={formData.stakeholders.corporate_interests}
              onChange={(items) => handleArrayInputChange('stakeholders', 'corporate_interests', items)}
            />
            
            <FormInput
              id="workplace_dynamics"
              label="Workplace Dynamics and Worker Sentiment"
              placeholder="Describe the workplace culture, worker attitudes, and organizing potential"
              value={formData.stakeholders.workplace_dynamics}
              onChange={(e) => handleInputChange('stakeholders', 'workplace_dynamics', e.target.value)}
              multiline
              rows={4}
            />
          </div>
        );
      
      case 'corporate_and_strategic':
        return (
          <div className="animate-fade-in">
            <h3 className="text-lg font-medium mb-4">Corporate & Strategic Analysis</h3>
            
            <FormInput
              id="corporate_motivations"
              label="Known or Suspected Corporate Motivations"
              placeholder="Describe corporate interests driving their position on this issue"
              value={formData.corporate_and_strategic.corporate_motivations}
              onChange={(e) => handleInputChange('corporate_and_strategic', 'corporate_motivations', e.target.value)}
              multiline
              rows={3}
            />
            
            <FormInput
              id="anti_union_tactics"
              label="Anti-Union Tactics or Labor Relations Strategy"
              placeholder="e.g., union avoidance consultants, captive audience meetings, intimidation"
              value={formData.corporate_and_strategic.anti_union_tactics}
              onChange={(e) => handleInputChange('corporate_and_strategic', 'anti_union_tactics', e.target.value)}
              multiline
              rows={3}
            />
            
            <FormInput
              id="corporate_vulnerabilities"
              label="Known Corporate Vulnerabilities or Pressure Points"
              placeholder="Describe reputation risks, regulatory issues, or stakeholder pressures"
              value={formData.corporate_and_strategic.corporate_vulnerabilities}
              onChange={(e) => handleInputChange('corporate_and_strategic', 'corporate_vulnerabilities', e.target.value)}
              multiline
              rows={3}
            />
          </div>
        );
      
      case 'campaign_outcome':
        return (
          <div className="animate-fade-in">
            <h3 className="text-lg font-medium mb-4">Desired Campaign Outcome</h3>
            
            <FormInput
              id="primary_goal"
              label="What is your primary campaign goal?"
              placeholder="e.g., secure union recognition, stop retaliation, improve working conditions"
              value={formData.campaign_outcome.primary_goal}
              onChange={(e) => handleInputChange('campaign_outcome', 'primary_goal', e.target.value)}
              required
              error={errors['campaign_outcome.primary_goal']}
            />
            
            <FormInput
              id="target_audience"
              label="Who is your primary target audience?"
              placeholder="e.g., corporate executives, board of directors, key investors, public opinion"
              value={formData.campaign_outcome.target_audience}
              onChange={(e) => handleInputChange('campaign_outcome', 'target_audience', e.target.value)}
              required
              error={errors['campaign_outcome.target_audience']}
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  const sections = [
    { id: 'core_issue_details', label: 'Core Campaign Details' },
    { id: 'documents_and_links', label: 'Documents & Links' },
    { id: 'stakeholders', label: 'Stakeholders' },
    { id: 'corporate_and_strategic', label: 'Corporate & Strategic' },
    { id: 'campaign_outcome', label: 'Campaign Outcome' },
  ];

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center mb-6 text-primary-700">
        <Users className="h-6 w-6 mr-2" />
        <h2 className="text-xl font-semibold">Corporate Campaign Intake Form</h2>
      </div>
      
      <div className="mb-8">
        <div className="flex overflow-x-auto">
          {sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                activeSection === section.id
                  ? 'text-primary-700 border-b-2 border-primary-500'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {index + 1}. {section.label}
            </button>
          ))}
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        {renderSection()}
        
        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={() => {
              const currentIndex = sections.findIndex(s => s.id === activeSection);
              if (currentIndex > 0) {
                setActiveSection(sections[currentIndex - 1].id);
              }
            }}
            className={`px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 ${
              activeSection === sections[0].id ? 'invisible' : ''
            }`}
            disabled={isSubmitting}
          >
            Previous
          </button>
          
          {activeSection === sections[sections.length - 1].id ? (
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                const currentIndex = sections.findIndex(s => s.id === activeSection);
                if (currentIndex < sections.length - 1) {
                  setActiveSection(sections[currentIndex + 1].id);
                }
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              Next
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CampaignForm;
